import { cloneDeep } from "lodash";
import moment from "moment";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { Option } from "../../../common/types/Option";
import { reportIncomeFilterOptions, reportIncomeIndicators, allIncomeClassificators, reportIncomeSegmentationOptions, reportIncomeServiceIndicatorKeys, getEffectiveIncomeForm } from "../report-income";
import { Draggable } from "../../../common/types/Draggable";
import { Indicator } from "../../../common/types/Indicator";
import { ReportIncomeTableDataRequest } from "../ReportIncomeTableDataRequest";
import { ReportIncomeJournalRequest } from "../journal/ReportIncomeJournalRequest";
import { ReportIncomeFilterSettingsForm } from "../settings-form/ReportIncomeFilterSettingsForm";
import { ReportIncomeFilterSettings } from "../settings/ReportIncomeFilterSettings";
import { BudgetType, MutuallyRedeemingLevel, Segmentation } from "../../../common/types/roots/fields";
import { ReportIncomeResponse } from "../ReportIncomeResponse";
import { ReportIncomeTableData } from "../table-data/table-data-types";

/** Разрезность отчёта — только Регион и КБК: порядок и active из сохранённого, недостающие — в конец неактивными */
function normalizeSegmentations(segmentations: Draggable<Segmentation>[] | null | undefined): Draggable<Segmentation>[] {
    const known = reportIncomeSegmentationOptions.map(it => it.value)
    const result: Draggable<Segmentation>[] = cloneDeep(
        (segmentations ?? []).filter(it => known.includes(it.value))
    )
    for (const option of reportIncomeSegmentationOptions) {
        if (!result.some(it => it.value === option.value)) {
            result.push({ ...cloneDeep(option), active: false })
        }
    }
    return result
}

/**
 * Показатели: порядок сохранённого, неизвестные ключи отбрасываются, недостающие вставляются
 * в каноническую позицию; у служебных колонок (action/kat/cls/pcl/spf) active берётся из канона.
 */
function normalizeIndicators(indicators: Indicator[] | null | undefined): Indicator[] {
    const canon = reportIncomeIndicators
    const result: Indicator[] = []
    for (const it of indicators ?? []) {
        const c = canon.find(x => x.key === it.key)
        if (!c || result.some(x => x.key === it.key)) {
            continue
        }
        result.push({ key: it.key, active: reportIncomeServiceIndicatorKeys.includes(it.key) ? c.active : !!it.active })
    }
    canon.forEach((c, i) => {
        if (result.some(x => x.key === c.key)) {
            return
        }
        let pos = 0
        for (let j = i - 1; j >= 0; j--) {
            const idx = result.findIndex(x => x.key === canon[j].key)
            if (idx >= 0) {
                pos = idx + 1
                break
            }
        }
        result.splice(pos, 0, { ...c })
    })
    return result
}

/** Поле "Округлять до" (b-form-input) отдаёт строку; пустое значение = не задано */
function toRoundUpTo(roundUp: boolean, value: number | string | null | undefined): number | null {
    if (!roundUp || value === null || value === undefined || String(value).trim() === '') {
        return null
    }
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
}

export class ReportIncomeFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportIncomeFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'INCOME') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'INCOME') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = []
        settings.mutuallyRedeemingLevels.forEach(level => {
            const option = options.mutuallyRedeemingLevels.find(it => it.value === level)!
            mutuallyRedeemingLevelOptions.push({...option})
        })

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption } 

        const regions: Option<string>[] = []
        settings.regions.forEach(region => {
            const option = options.regions.find(it => it.value === region)!
            regions.push({...option})
        })

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        const statuses = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        const budgetTypes: Option<BudgetType>[] = []
        settings.budgetTypes.forEach(budget => { // TODO: just filter
            const budgetTypeOption = options.budgetTypes.find(item => item.value === budget)!
            budgetTypes.push({...budgetTypeOption})
        })

        // 2. Формирование
        const form: ReportIncomeFilterSettingsForm = {
            // Base:
            key: 'INCOME',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            mutuallyRedeeming: mutuallyRedeemingOption,
            measureUnit: measureUnitOption,
            roundUp: isRoundUp,
            roundUpTo: toRoundUpTo(isRoundUp, settings.roundUpTo),
            // Custom:
            regions: regions,
            date: new Date(settings.date),
            budgetTypes: budgetTypes,
            segmentations: normalizeSegmentations(settings.segmentations),
            decodingActualRegionalBudget: settings.decodingActualRegionalbudget,
            isTree: settings.isTree,
            statuses: statuses,
            regionsTotal: settings.regionsTotal,
            prevYear: settings.prevYear,
            indicators: normalizeIndicators(settings.indicators),
            incomes: cloneDeep(settings.incomes),
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            sortByRegionCode: settings.sortByRegionCode ?? false,
            nameBeforeKbk: settings.nameBeforeKbk ?? false,
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'INCOME') {
            throw new Error('error_type_filter_settingsForm')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }
        if (form.regions.length === 0) {
            throw new Error('error_select_region')
        }
        if (!form.date) {
            throw new Error('error_input_date')
        }

        if (!form.segmentations) {
            throw new Error('error_input_segmentation')
        }

        if (form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }

        const roundUpTo: number | null = toRoundUpTo(form.roundUp, form.roundUpTo)
        
        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: ReportIncomeFilterSettings = {
            // Base:
            reportName: 'INCOME',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            budgetTypes: form.budgetTypes.map(it => it.value),
            // Custom:
            regions: form.regions.map(it => it.value),
            date: strDate,
            indicators: cloneDeep(form.indicators),
            segmentations: normalizeSegmentations(form.segmentations),
            decodingActualRegionalbudget: form.decodingActualRegionalBudget ?? false,
            isTree: form.isTree ?? false,
            regionsTotal: form.regionsTotal ?? false,
            statuses: form.statuses.map(status => status.value),
            prevYear: form.prevYear ?? false,
            incomes: cloneDeep(form.incomes),
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels?.map(it => it.value) ?? [],
            sortByRegionCode: form.sortByRegionCode ?? false,
            nameBeforeKbk: form.nameBeforeKbk ?? false,
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportIncomeFilterOptions)

        const emptyForm: ReportIncomeFilterSettingsForm = {
            key: 'INCOME',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'INCOME')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'INCOMES')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            roundUp: false,
            roundUpTo: null,
            budgetTypes: [],
            // Custom fields            
            regions: [],
            segmentations: cloneDeep(reportIncomeSegmentationOptions),
            date: null,
            decodingActualRegionalBudget: false,
            isTree: false,
            regionsTotal: false,
            statuses: [options.statuses.find(it => it.value === '39')!],
            prevYear: false,
            indicators: cloneDeep(reportIncomeIndicators),
            incomes: cloneDeep(allIncomeClassificators).map(it =>
                it.key === 'kat' ? { ...it, selected: [] } : it
            ),
            mutuallyRedeemingLevels: [],
            sortByRegionCode: false,
            nameBeforeKbk: false,
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'INCOME') {
            throw new Error('error_type_filter_settingsForm')
        }
        
        const date = form.date
        if (!date) {
            throw new Error('error_input_date')
        }
        
        if ('regions' in form && form.regions.length === 0) {
            throw new Error('error_input_region')
        }

        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }

        if ('regions' in form && form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }

        const roundUpTo: number | null = toRoundUpTo(form.roundUp, form.roundUpTo)

        if (!form.segmentations?.filter(it => it.active).length) {
            throw new Error('error_input_segmentation')
        }

        if (form.decodingActualRegionalBudget == null) {
            throw new Error('error_input_decoding_actual_regional_budget')
        }

        if (form.isTree == null) {
            throw new Error('error_input_tree_struct')
        }

        if (form.regionsTotal == null) {
            throw new Error('error_input_city_total')
        }

        const cutness: string[] | undefined = form.segmentations?.filter(v => v.active).map(v => v.value)
        if (cutness == null) {
            throw new Error('error_unsupported_segmentation_type')
        }

        const strDate = moment(date).format('YYYY-MM-DD')

        // Недоступные при текущей разрезности переключатели не должны влиять на отчёт:
        // в запрос, журнал и filter_settings журнала уходят эффективные значения (шаблон хранит исходные)
        const effective = getEffectiveIncomeForm(form)

        const tempIncomes = form.incomes
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        const params: ReportIncomeTableDataRequest = {
            incomes: tempIncomes,
            date: strDate,
            regions: form.regions.map(it => it.value),
            vzk: form.mutuallyRedeeming?.value,
            measureUnit: form.measureUnit.value,
            cutness: cutness,
            roundTo: roundUpTo,
            decodingActualRegionalBudget: effective.decodingActualRegionalBudget,
            isTree: effective.isTree,
            statuses: form.statuses.map(status => status.value),
            regionsTotal: effective.regionsTotal,
            prevYear: effective.prevYear,
            budget_types: form.budgetTypes.map(it => it.value),
            mutually_redeeming_levels: form.mutuallyRedeemingLevels.map(it => it.value),
            dataSource: form.dataSource.value,
            sort_by_region_code: form.sortByRegionCode ?? false,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/income',
            {
                method: 'POST',
                cache: 'no-cache',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseData: ReportIncomeResponse = await response.json()
        const tableData: ReportIncomeTableData = responseData

        if ('template3_items' in tableData){
            tableData.template3_items?.forEach(r => {
                r.visible = true
                r.is_expanded = true
            })
        }
        if ('template4_items_structured_regions' in tableData){
            tableData.template4_items_structured_regions?.forEach(r => {
                if ('childs' in r) {            
                    r.is_childs_visible = true
                }
            })
        }

        const settings: FilterSettings = this.formToSettings(cloneDeep(effective))
        const journalParams: ReportIncomeJournalRequest = {
            reportName: "INCOME",

            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // indicators_order: form.indicators.filter(it => it.active).map(it => it.key),
            
            classificators: tempIncomes,
            regions: form.regions.map(it => it.value),
            report_date: strDate,
            measure_unit: form.measureUnit.value,
            actually_regional: tableData.actually_regional ?? null,
            region_budget: tableData.region_budget ?? null,
            template1_items: tableData.template1_items ?? null,
            template1_items_total: tableData.template1_items_total ?? null,
            template1_regional_budget_decrypt_items: tableData.template1_regional_budget_decrypt_items ?? null,
            template2_items: tableData.template2_items ?? null,
            template1_items_districts_total: tableData.template1_items_districts_total ?? null,
            template1_items_cities_total: tableData.template1_items_cities_total ?? null,
            template2_items_total: tableData.template2_items_total ?? null,
            template2_items_tree: tableData.template2_items_tree ?? null,
            template3_items: tableData.template3_items ?? null,
            template4_items: tableData.template4_items ?? null,
            template4_items_structured_dspk: tableData.template4_items_structured_dspk?? null,
            template4_items_structured_regions: tableData.template4_items_structured_regions ?? null,
            decodingActualRegionalBudget: effective.decodingActualRegionalBudget,
            budget_types: form.budgetTypes.map(it => it.value),
            cutness: cutness,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
